import React, { useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useCatalog } from "../context/CatalogContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FloatingWhatsApp from "../components/FloatingWhatsApp";
import DesignCard from "../components/DesignCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const absolutize = (p) =>
  !p ? "" : p.startsWith("http") ? p : `${BACKEND_URL}${p}`;

/**
 * Dedicated page for a single Limited-Time Collection.
 * Route: /limited/:id
 * Shows the collection's cover, tagline, and every design bound to it —
 * scoped-only designs (limitedCollectionId set) + any picked from the
 * general catalog.
 */
export default function LimitedCollectionPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const catalog = useCatalog();
  const {
    limitedCollections = [],
    customDesigns = [],
    limitedScopedDesigns = [],
    getDesignsMerged,
    categoriesMerged,
  } = catalog;

  const coll = useMemo(
    () => limitedCollections.find((c) => c.id === id),
    [limitedCollections, id]
  );

  const designs = useMemo(() => {
    if (!coll) return [];
    const pool = new Map();
    limitedScopedDesigns.forEach((d) => pool.set(d.id, d));
    customDesigns.forEach((d) => { if (!pool.has(d.id)) pool.set(d.id, d); });
    if (getDesignsMerged && categoriesMerged) {
      const all = categoriesMerged.flatMap((cat) =>
        cat.subCategories.flatMap((sub) => getDesignsMerged(cat.slug, sub.slug))
      );
      all.forEach((d) => { if (!pool.has(d.id)) pool.set(d.id, d); });
    }
    return (coll.designIds || []).map((did) => pool.get(did)).filter(Boolean);
  }, [coll, customDesigns, limitedScopedDesigns, getDesignsMerged, categoriesMerged]);

  if (!coll) {
    return (
      <div className="alankar-site">
        <Header />
        <div className="wrap" style={{ padding: "140px 28px", textAlign: "center" }}>
          <h2>Collection not found</h2>
          <p style={{ marginTop: 12 }}>
            <button className="btn btn-outline" onClick={() => navigate("/")}>
              <ArrowLeft size={16} /> Back to Home
            </button>
          </p>
        </div>
        <Footer />
        <FloatingWhatsApp />
      </div>
    );
  }

  return (
    <div className="alankar-site">
      <Header />
      <section className="ltc-page">
        <div className="wrap">
          <nav className="cat-crumbs" data-testid="ltc-page-crumbs">
            <Link to="/">Home</Link>
            <span>/</span>
            <span>Limited</span>
            <span>/</span>
            <span>{coll.name}</span>
          </nav>

          <header className="ltc-page-head">
            {coll.cover && (
              <div className="ltc-page-cover" aria-hidden="true">
                <img src={absolutize(coll.cover)} alt="" />
              </div>
            )}
            <div>
              <span className="ltc-badge">Limited time</span>
              <h1 data-testid="ltc-page-title">{coll.name}</h1>
              {coll.tagline && <p>{coll.tagline}</p>}
              {coll.endDate && (
                <p className="ltc-page-end" data-testid="ltc-page-end">
                  Available until <strong>{coll.endDate}</strong>
                </p>
              )}
            </div>
          </header>

          {designs.length > 0 ? (
            <div className="design-grid" data-testid="ltc-page-grid">
              {designs.map((d) => (
                <DesignCard key={d.id} design={d} />
              ))}
            </div>
          ) : (
            <p className="ltc-empty">
              Curated pieces coming soon — the collection is being finalised in-store.
            </p>
          )}
        </div>
      </section>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
